import React, { useEffect, useMemo, useState } from 'react';

import api from '../api';
import AdminGoalsDashboard from './AdminGoalsDashboard';
import CreateGoalModal from './CreateGoalModal';
import GoalDetailView from './GoalDetailView';
import GoalNotificationsPanel from './GoalNotificationsPanel';
import { formatDate, formatNumber, GOALS_TEXT_SCOPE_STYLES, MODULE_OPTIONS, prettyLabel, statusStyle } from './shared';

export default function AdminGoalsPage() {
  const [goals, setGoals] = useState([]);
  const [filters, setFilters] = useState({ module: '', status: '', search: '' });
  const [selectedGoalId, setSelectedGoalId] = useState(null);
  const [selectedTab, setSelectedTab] = useState('overview');
  const [showCreate, setShowCreate] = useState(false);

  const loadGoals = async () => {
    try {
      const response = await api.get('/goals', { params: filters });
      setGoals(response.data?.data || []);
    } catch {
      setGoals([]);
    }
  };

  useEffect(() => {
    loadGoals();
  }, [filters.module, filters.status, filters.search]);

  const summary = useMemo(() => ({
    total: goals.length,
    highPriority: goals.filter((goal) => Number(goal.priority || 3) <= 2).length,
    overdue: goals.filter((goal) => goal.status === 'OVERDUE').length,
    unassigned: goals.filter((goal) => !goal.assigned_to_user_id).length,
  }), [goals]);

  if (selectedGoalId) {
    return <GoalDetailView goalId={selectedGoalId} canManage adminView initialTab={selectedTab} onClose={() => { setSelectedGoalId(null); setSelectedTab('overview'); }} onRefresh={loadGoals} />;
  }

  return (
    <div className="goals-text-scope" style={{ display: 'grid', gap: 20 }}>
      <style>{GOALS_TEXT_SCOPE_STYLES}</style>

      <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 0.8fr', gap: 18 }}>
        <div style={{ background: 'linear-gradient(135deg,#dbeafe 0%,#eff6ff 55%,#ffffff 100%)', border: '1px solid #bfdbfe', borderRadius: 24, padding: 22 }}>
          <div style={{ fontSize: 12, fontWeight: 900, letterSpacing: '0.08em' }}>ADMIN COMMAND CENTER</div>
          <div style={{ marginTop: 8, fontSize: 30, fontWeight: 900 }}>Enterprise Goals Oversight Across Every Module</div>
          <div style={{ marginTop: 10, fontSize: 15, lineHeight: 1.55 }}>
            Admin is the supervisory layer for the whole company. Create goals, assign accountability, review proof and progress, and intervene only through governance controls instead of module-level execution actions.
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 18 }}>
            <button onClick={() => setShowCreate(true)} style={{ border: 0, borderRadius: 14, background: '#1d4ed8', color: '#fff', padding: '13px 16px', fontWeight: 800, cursor: 'pointer' }}>
              Create New Goal
            </button>
            <div style={{ padding: '13px 16px', borderRadius: 14, background: '#fff', border: '1px solid #cbd5e1', fontWeight: 700 }}>
              Review all modules without becoming part of daily execution
            </div>
          </div>
        </div>

        <GoalNotificationsPanel />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 14 }}>
        {[
          ['Total Goals', summary.total, '#0f172a'],
          ['High Priority', summary.highPriority, '#b45309'],
          ['Overdue Goals', summary.overdue, '#b91c1c'],
          ['Unassigned Goals', summary.unassigned, '#7c3aed'],
        ].map(([label, value, color]) => (
          <div key={label} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 18, padding: 18 }}>
            <div style={{ fontSize: 13, fontWeight: 800 }}>{label}</div>
            <div style={{ marginTop: 8, fontSize: 32, fontWeight: 900, color }}>{value}</div>
          </div>
        ))}
      </div>

      <AdminGoalsDashboard />

      <div style={{ display: 'grid', gridTemplateColumns: '0.95fr 1.05fr', gap: 18 }}>
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 20, padding: 18 }}>
          <div style={{ fontSize: 18, fontWeight: 900, marginBottom: 14 }}>Admin Filters</div>
          <div style={{ display: 'grid', gap: 12 }}>
            <select value={filters.module} onChange={(e) => setFilters({ ...filters, module: e.target.value })} style={{ padding: 12, borderRadius: 14, border: '1px solid #cbd5e1' }}>
              <option value="">All Modules</option>
              {MODULE_OPTIONS.map((option) => <option key={option} value={option}>{prettyLabel(option)}</option>)}
            </select>
            <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} style={{ padding: 12, borderRadius: 14, border: '1px solid #cbd5e1' }}>
              <option value="">All Statuses</option>
              {['DRAFT', 'ACTIVE', 'ON_TRACK', 'AT_RISK', 'OVERDUE', 'COMPLETED', 'CANCELLED'].map((option) => <option key={option} value={option}>{prettyLabel(option)}</option>)}
            </select>
            <input value={filters.search} onChange={(e) => setFilters({ ...filters, search: e.target.value })} placeholder="Search title or description" style={{ padding: 12, borderRadius: 14, border: '1px solid #cbd5e1' }} />
            <div style={{ borderRadius: 16, background: '#f8fafc', border: '1px solid #e2e8f0', padding: 14 }}>
              <div style={{ fontWeight: 800, marginBottom: 8 }}>Why this Admin view is different</div>
              <div style={{ fontSize: 14, lineHeight: 1.5 }}>
                Admin sees all modules, governance alerts, assignment gaps, proof review, and quality scoring. Module teams handle execution updates, proof uploads, and task movement inside their own workspaces.
              </div>
            </div>
          </div>
        </div>

        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 20, padding: 18 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 14 }}>
            <div>
              <div style={{ fontSize: 18, fontWeight: 900 }}>Goal Governance Queue</div>
              <div style={{ fontSize: 14 }}>Open any goal for executive review, task governance, scoring, and accountability checks.</div>
            </div>
            <div style={{ fontWeight: 800 }}>{goals.length} results</div>
          </div>

          <div style={{ display: 'grid', gap: 12, maxHeight: 820, overflowY: 'auto', paddingRight: 4 }}>
            {goals.length ? goals.map((goal) => {
              const style = statusStyle(goal.status);
              return (
                <div key={goal.id} style={{ border: '1px solid #e2e8f0', borderRadius: 16, background: '#fff', padding: 16 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                    <div>
                      <div style={{ fontWeight: 900, fontSize: 17 }}>{goal.title}</div>
                      <div style={{ marginTop: 4, fontSize: 14 }}>{goal.description || 'No description provided.'}</div>
                    </div>
                    <span style={{ padding: '6px 10px', borderRadius: 999, background: style.bg, color: style.text, border: `1px solid ${style.border}`, fontWeight: 800 }}>
                      {prettyLabel(goal.status)}
                    </span>
                  </div>

                  <div style={{ marginTop: 14, display: 'grid', gridTemplateColumns: 'repeat(6, minmax(0, 1fr))', gap: 10 }}>
                    <div><strong>{prettyLabel(goal.assigned_module)}</strong><br />Module</div>
                    <div><strong>{goal.assigned_to_user_id || 'Unassigned'}</strong><br />Owner</div>
                    <div><strong>{goal.priority || 3}</strong><br />Priority</div>
                    <div><strong>{formatNumber(goal.completion_percentage)}%</strong><br />Completion</div>
                    <div><strong>{formatNumber(goal.current_value)}</strong><br />Current</div>
                    <div><strong>{formatDate(goal.end_date)}</strong><br />Deadline</div>
                  </div>
                  <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
                    <button onClick={() => { setSelectedGoalId(goal.id); setSelectedTab('overview'); }} style={{ border: 0, borderRadius: 12, background: '#1d4ed8', color: '#fff', padding: '10px 14px', fontWeight: 800, cursor: 'pointer' }}>
                      Review Goal
                    </button>
                    <button onClick={() => { setSelectedGoalId(goal.id); setSelectedTab('tasks'); }} style={{ border: '1px solid #cbd5e1', borderRadius: 12, background: '#fff', color: '#0f172a', padding: '10px 14px', fontWeight: 800, cursor: 'pointer' }}>
                      Review Tasks
                    </button>
                  </div>
                </div>
              );
            }) : <div style={{ fontSize: 15 }}>No goals match the current filters.</div>}
          </div>
        </div>
      </div>

      <CreateGoalModal open={showCreate} onClose={() => setShowCreate(false)} onCreated={loadGoals} />
    </div>
  );
}
